import { useState, useMemo, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EntityType } from '@/types/link';
import { useAssetsStore } from '@/stores/assetsStore';
import { useRisks } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useSupplierStore } from '@/stores/supplierStore';
import { useSiteStore } from '@/stores/siteStore';
import { useZoneStore } from '@/stores/zoneStore';
import { useSystemStore } from '@/stores/systemStore';
import { Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import type { Node } from '@xyflow/react';
import type { NodeBase } from '@/types/assets';
import type { Risk } from '@/hooks/queries/useRisks';
import type { Supplier } from '@/types/supplier';
import type { Site } from '@/types/site';
import type { Zone } from '@/types/zone';
import type { System } from '@/types/system';

type EntityOption = {
  id: string;
  name: string;
};

const ASSET_NODE_TYPES: ReadonlyArray<NodeBase['type']> = [
  'endpoint',
  'system',
  'shared_infrastructure',
  'supplier',
];

interface EntitySelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (entityType: EntityType, entityIds: string[]) => void;
  allowedTypes?: EntityType[];
  multiSelect?: boolean;
  title?: string;
}

export function EntitySelectorDialog({
  open,
  onOpenChange,
  onSelect,
  allowedTypes = ['asset', 'risk', 'control', 'supplier', 'incident'],
  multiSelect = true,
  title = 'Select Entities to Link',
}: EntitySelectorDialogProps) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<EntityType>(allowedTypes[0]);
  const [controls, setControls] = useState<EntityOption[]>([]);
  const [incidents, setIncidents] = useState<EntityOption[]>([]);

  const { nodes } = useAssetsStore();
  const { organization } = useAuth();
  const { risks } = useRisks(organization?.id);
  const { suppliers } = useSupplierStore();
  const { sites } = useSiteStore();
  const { zones } = useZoneStore();
  const { systems } = useSystemStore();

  // Fetch controls and incidents
  useEffect(() => {
    let isActive = true;

    const loadEntities = async () => {
      if (allowedTypes.includes('control')) {
        const { data, error } = await supabase
          .from('controls')
          .select('id, title')
          .order('title');

        if (!error && isActive) {
          setControls((data ?? []).map(item => ({ id: item.id, name: item.title })));
        }
      } else if (isActive) {
        setControls([]);
      }

      if (allowedTypes.includes('incident')) {
        const { data, error } = await supabase
          .from('incidents')
          .select('id, title')
          .order('title');

        if (!error && isActive) {
          setIncidents((data ?? []).map(item => ({ id: item.id, name: item.title })));
        }
      } else if (isActive) {
        setIncidents([]);
      }
    };

    void loadEntities();

    return () => {
      isActive = false;
    };
  }, [allowedTypes]);

  const getEntitiesForType = useCallback((type: EntityType): EntityOption[] => {
    switch (type) {
      case 'asset':
        return (nodes as Node<NodeBase>[])
          .filter(node => node.data?.type && ASSET_NODE_TYPES.includes(node.data.type))
          .map(node => ({ id: node.id, name: node.data?.label ?? 'Unnamed Asset' }));
      case 'site':
        return (sites as Site[]).map(site => ({ id: site.id, name: site.name }));
      case 'zone':
        return (zones as Zone[]).map(zone => ({ id: zone.id, name: zone.name }));
      case 'system':
        return (systems as System[]).map(system => ({ id: system.id, name: system.name }));
      case 'risk':
        return (risks as Risk[]).map(risk => ({ id: risk.id, name: risk.title }));
      case 'control':
        return controls;
      case 'supplier':
        return (suppliers as Supplier[]).map(supplier => ({ id: supplier.id, name: supplier.name }));
      case 'incident':
        return incidents;
      default:
        return [];
    }
  }, [controls, incidents, nodes, risks, sites, suppliers, systems, zones]);

  const filteredEntities = useMemo(() => {
    const entities = getEntitiesForType(activeTab);
    if (!search) return entities;
    return entities.filter(entity =>
      entity.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [activeTab, getEntitiesForType, search]);

  const toggleSelection = useCallback((id: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      if (!multiSelect) {
        newSelection.clear();
      }
      newSelection.add(id);
    }
    setSelectedIds(newSelection);
  }, [multiSelect, selectedIds]);

  const handleConfirm = useCallback(() => {
    onSelect(activeTab, Array.from(selectedIds));
    setSelectedIds(new Set());
    setSearch('');
    onOpenChange(false);
  }, [activeTab, onOpenChange, onSelect, selectedIds]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as EntityType)}>
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${allowedTypes.length}, 1fr)` }}>
            {allowedTypes.map(type => (
              <TabsTrigger key={type} value={type} className="capitalize">
                {type.replace('_', ' ')}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {allowedTypes.map(type => (
            <TabsContent key={type} value={type} className="mt-4">
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-2">
                  {filteredEntities.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No {type}s found
                    </p>
                  ) : (
                    filteredEntities.map(entity => (
                      <div
                        key={entity.id}
                        className="flex items-center space-x-2 p-2 rounded hover:bg-accent cursor-pointer"
                        onClick={() => toggleSelection(entity.id)}
                      >
                        <Checkbox
                          checked={selectedIds.has(entity.id)}
                          onCheckedChange={() => toggleSelection(entity.id)}
                        />
                        <span className="text-sm flex-1">{entity.name}</span>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          ))}
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={selectedIds.size === 0}>
            Link {selectedIds.size} {selectedIds.size === 1 ? 'Item' : 'Items'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
